
import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import {
    YardInventoryItem,
    YardLocations,
    YardMovementLogs,
    QCStatus,
    YardInventoryItemsData,
    YardInventoryItemsPatch,
} from "../types/YardTypes";

export class YardInventoryStore {
    rootStore: RootStore;

    // Loading states
    loading = false;
    error: string | null = null;

    // Selected items for operations
    selectedInventoryItemId: number | null = null;
    selectedLocationId: number | null = null;
    selectedItemIds: number[] = [];

    // Filter and search state
    filterQcStatus: QCStatus | "all" = "all";
    filterLocationId: number | "all" = "all";
    searchQuery: string = "";

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    // Service store accessors
    get yardInventoryItemsStore(): ServiceStore {
        return this.rootStore.yardInventoryItemsStore as ServiceStore;
    }

    get yardLocationsStore(): ServiceStore {
        return this.rootStore.yardLocationsStore as ServiceStore;
    }

    get yardMovementLogsStore(): ServiceStore {
        return this.rootStore.yardMovementLogsStore as ServiceStore;
    }

    get productsStore(): ServiceStore {
        return this.rootStore.productsStore as ServiceStore;
    }

    // Computed values
    get inventoryItems(): YardInventoryItem[] {
        return this.yardInventoryItemsStore.entities as unknown[] as YardInventoryItem[];
    }

    get yardLocations(): YardLocations[] {
        return this.yardLocationsStore.entities as unknown[] as YardLocations[];
    }

    get movementLogs(): YardMovementLogs[] {
        return this.yardMovementLogsStore.entities as unknown[] as YardMovementLogs[];
    }

    get products(): any[] {
        return this.productsStore.entities as unknown[] as any[];
    }

    get selectedInventoryItem(): YardInventoryItem | null {
        if (!this.selectedInventoryItemId) return null;
        return this.inventoryItems.find(item => item.id === this.selectedInventoryItemId) || null;
    }

    get selectedLocation(): YardLocations | null {
        if (!this.selectedLocationId) return null;
        return this.yardLocations.find(loc => loc.id === this.selectedLocationId) || null;
    }

    get filteredInventoryItems(): YardInventoryItem[] {
        let filtered = this.inventoryItems;

        // Apply QC status filter
        if (this.filterQcStatus !== "all") {
            filtered = filtered.filter(item => item.qcStatus === this.filterQcStatus);
        }

        // Apply location filter
        if (this.filterLocationId !== "all") {
            filtered = filtered.filter(item => item.currentLocationId === this.filterLocationId);
        }

        // Apply search filter
        if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                (item.lotNumber && item.lotNumber.toLowerCase().includes(query)) ||
                (item.notes && item.notes.toLowerCase().includes(query)) ||
                item.productId.toString().includes(query)
            );
        }

        return filtered;
    }

    get inventoryByQcStatus() {
        const statusGroups = this.inventoryItems.reduce((acc: any, item: any) => {
            acc[item.qcStatus] = (acc[item.qcStatus] || 0) + 1;
            return acc;
        }, {} as Record<QCStatus, number>);

        return {
            pending: statusGroups.pending || 0,
            passed: statusGroups.passed || 0,
            failed: statusGroups.failed || 0,
            rework: statusGroups.rework || 0,
        };
    }

    get inventoryByLocation() {
        const locationGroups = this.inventoryItems.reduce((acc: any, item: any) => {
            const location = this.yardLocations.find(loc => loc.id === item.currentLocationId);
            const locationName = location ? location.name : `Location ${item.currentLocationId}`;
            acc[locationName] = (acc[locationName] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        return locationGroups;
    }

    get totalQuantity(): number {
        return this.inventoryItems.reduce((total, item) => total + item.quantity, 0);
    }

    get recentMovements(): YardMovementLogs[] {
        return this.movementLogs
            .slice()
            .sort((a, b) => new Date(b.movementDateTime).getTime() - new Date(a.movementDateTime).getTime())
            .slice(0, 10);
    }

    // Helper methods
    getLocationName = (locationId: number): string => {
        const location = this.yardLocations.find(loc => loc.id === locationId);
        return location ? location.name : `Location ${locationId}`;
    };

    getProductName = (productId: number): string => {
        const product = this.products.find(p => p.id === productId);
        return product ? product.name || `Product ${productId}` : `Product ${productId}`;
    };

    getItemMovementHistory = async (inventoryItemId: number): Promise<YardMovementLogs[]> => {
        try {
            const result = await this.yardMovementLogsStore.find({
                query: {
                    inventoryItemId,
                    $sort: { movementDateTime: 1 }
                }
            });
            return (result?.data || result) as unknown as YardMovementLogs[];
        } catch (error) {
            console.error('Failed to get item movement history:', error);
            return [];
        }
    };

    // Actions
    setSelectedInventoryItem = (itemId: number | null) => {
        this.selectedInventoryItemId = itemId;
    };

    setSelectedLocation = (locationId: number | null) => {
        this.selectedLocationId = locationId;
    };

    setFilterQcStatus = (status: QCStatus | "all") => {
        this.filterQcStatus = status;
    };

    setFilterLocationId = (locationId: number | "all") => {
        this.filterLocationId = locationId;
    };

    setSearchQuery = (query: string) => {
        this.searchQuery = query;
    };

    setLoading = (loading: boolean) => {
        this.loading = loading;
    };

    setError = (error: string | null) => {
        this.error = error;
    };

    // Async actions
    refreshData = async () => {
        this.setLoading(true);
        this.setError(null);

        try {
            await Promise.all([
                this.yardInventoryItemsStore.find({ query: { $limit: 200, $sort: { dateAdded: -1 } } }),
                this.yardLocationsStore.find({ query: { $limit: 100 } }),
                this.yardMovementLogsStore.find({ query: { $limit: 100, $sort: { movementDateTime: -1 } } }),
                this.productsStore.find({ query: { $limit: 200 } }),
            ]);
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to refresh data');
            console.error('Failed to refresh inventory data:', error);
        } finally {
            this.setLoading(false);
        }
    };

    createInventoryItem = async (itemData: YardInventoryItemsData) => {
        try {
            const newItem = await this.yardInventoryItemsStore.create({
                ...itemData,
                dateAdded: new Date().toISOString(),
            });

            await this.refreshData();
            return newItem;
        } catch (error) {
            throw new Error(`Failed to create inventory item: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    updateInventoryItem = async (itemId: number, updates: YardInventoryItemsPatch) => {
        try {
            await this.yardInventoryItemsStore.patch(itemId, updates as unknown as Parameters<typeof this.yardInventoryItemsStore.patch>[1]);
            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to update inventory item: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    deleteInventoryItem = async (itemId: number) => {
        try {
            await this.yardInventoryItemsStore.remove(itemId);
            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to delete inventory item: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    moveInventoryItem = async (itemId: number, toLocationId: number, notes?: string) => {
        const item = this.inventoryItems.find(i => i.id === itemId);
        if (!item) {
            throw new Error('Inventory item not found');
        }

        try {
            // Create movement log
            await this.yardMovementLogsStore.create({
                inventoryItemId: itemId,
                fromLocationId: item.currentLocationId,
                toLocationId: toLocationId,
                movedByUserId: this.rootStore.authStore?.currentUser?.id || 1,
                quantityMoved: item.quantity,
                notes: notes,
            });

            // Update inventory item location
            await this.yardInventoryItemsStore.patch(itemId, {
                currentLocationId: toLocationId,
                lastMoved: new Date().toISOString(),
            });

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to move inventory item: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    updateQcStatus = async (itemId: number, qcStatus: QCStatus, notes?: string) => {
        try {
            await this.yardInventoryItemsStore.patch(itemId, {
                qcStatus,
                ...(notes && { notes }),
            });

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to update QC status: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    // Batch operations
    batchDeleteItems = async (itemIds: number[]) => {
        try {
            this.setLoading(true);
            for (const id of itemIds) {
                await this.yardInventoryItemsStore.remove(id);
            }
            await this.refreshData();
            this.selectedItemIds = [];
        } catch (error) {
            throw new Error(`Failed to delete items: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            this.setLoading(false);
        }
    };

    batchUpdateQCStatus = async (itemIds: number[], newStatus: QCStatus) => {
        try {
            this.setLoading(true);
            for (const id of itemIds) {
                await this.yardInventoryItemsStore.patch(id, { qcStatus: newStatus });
            }
            await this.refreshData();
            this.selectedItemIds = [];
        } catch (error) {
            throw new Error(`Failed to update QC status: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            this.setLoading(false);
        }
    };

    batchMoveItems = async (itemIds: number[], toLocationId: number) => {
        try {
            this.setLoading(true);
            for (const id of itemIds) {
                const item = this.inventoryItems.find(i => i.id === id);
                if (item) {
                    await this.yardInventoryItemsStore.patch(id, { currentLocationId: toLocationId });
                    // Log the movement
                    await this.yardMovementLogsStore.create({
                        inventoryItemId: id,
                        fromLocationId: item.currentLocationId,
                        toLocationId: toLocationId,
                        movedByUserId: (this.rootStore as any).userStore?.currentUser?.id || 1,
                        quantityMoved: item.quantity,
                        notes: 'Batch move operation',
                    });
                }
            }
            await this.refreshData();
            this.selectedItemIds = [];
        } catch (error) {
            throw new Error(`Failed to move items: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            this.setLoading(false);
        }
    };

    setSelectedItemIds = (ids: number[]) => {
        this.selectedItemIds = ids;
    };

    clear = () => {
        runInAction(() => {
            this.selectedInventoryItemId = null;
            this.selectedLocationId = null;
            this.selectedItemIds = [];
            this.filterQcStatus = "all";
            this.filterLocationId = "all";
            this.searchQuery = "";
            this.error = null;
            this.loading = false;
        });
    };
}

export default YardInventoryStore;
